/*
 * Copyright (c) 2022 E-WIND - Thais Watanabe.
 * All rights reserved.
 */
import React, { useState, useEffect, Fragment } from "react";
import { API, Auth, Storage, graphqlOperation } from "aws-amplify";
import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { PaperClipIcon } from "@heroicons/react/24/outline";
import moment from "moment";
import toast from "react-hot-toast";

import { createPhoto, updatePhoto } from "src/graphql/mutations";

/**
 * The PhotoUpload component
 *
 * @author Thais Watanabe
 */
const PhotoUpload = (props) => {
  const {
    updateSlide,
    selectedItem,
    isSlideOpen,
    turbineInspection,
    photos,
    setPhotos,
    selectedPhoto,
    setSelectedPhoto,
  } = props;

  const [selectedPart, setSelectedPart] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState(null);
  const [caption, setCaption] = useState("");
  const [hasErrors, setHasErrors] = useState(false);
  const [loggedUser, setLoggedUser] = useState("");

  /**
   * useEffect
   */
  useEffect(() => {
    Auth.currentUserInfo()
      .then((result) => {
        setLoggedUser(result.username);
      })
      .catch((error) => console.log(error));

    return () => {
      setLoggedUser("");
    };
  }, []);

  /**
   * useEffect
   */
  useEffect(() => {
    setSelectedPart(selectedItem);
    setSelectedFiles(null);
    setCaption(selectedPhoto ? selectedPhoto.caption : "");
    setHasErrors(false);

    return () => {
      setSelectedPart([]);
      setSelectedFiles(null);
      setCaption("");
      setHasErrors(false);
    };
  }, [selectedItem, selectedPhoto]);

  /**
   * submit
   */
  async function submit() {
    var error = false;

    if (caption.length < 1) {
      setHasErrors(true);
      error = true;
    } else {
      setHasErrors(false);
      error = false;
    }

    if (!error) {
      if (selectedPhoto) {
        if (!selectedFiles) {
          selectedPhoto.caption = caption;
          changePhoto(selectedPhoto);
        } else {
          const s3FilePath = `${turbineInspection.id}/${selectedPart.name}_${selectedFiles.name}`;

          selectedPhoto.caption = caption;
          selectedPhoto.s3Path = "public/" + s3FilePath;

          Storage.put(s3FilePath, selectedFiles)
            .then((result) => {
              try {
                changePhoto(selectedPhoto);
              } catch (err) {
                console.log(err);
                toast.error("エラーが発生しました");
              }
            })
            .catch((err) => console.log(err));
        }
      } else {
        const now = moment().utc().format("YYYY-MM-DDTHH:mm:ss") + "Z";

        const s3FilePath = `${turbineInspection.id}/${selectedPart.name}_${now}_${selectedFiles.name}`;

        const inputPhoto = {
          turbineInspectionID: turbineInspection.id,
          modelNumber: selectedPart.modelNumber,
          modelItemName: selectedPart.name,
          s3Path: "public/" + s3FilePath,
          caption: caption,
          author: loggedUser,
          type: "confirmation",
        };

        Storage.put(s3FilePath, selectedFiles)
          .then((result) => {
            try {
              addPhoto(inputPhoto);
            } catch (err) {
              console.log(err);
              toast.error("エラーが発生しました");
            }
          })
          .catch((err) => console.log(err));
      }
    }
  }

  /**
   * addPhoto
   * @param {*} inputPhoto
   */
  async function addPhoto(inputPhoto) {
    const result = await API.graphql(
      graphqlOperation(createPhoto, { input: inputPhoto })
    );
    const newPhoto = result.data.createPhoto;
    setPhotos([...photos, newPhoto]);
    toast.success("保存しました");
    onClose();
  }

  /**
   * changePhoto
   * @param {*} inputPhoto
   */
  async function changePhoto(inputPhoto) {
    const result = await API.graphql(
      graphqlOperation(updatePhoto, { input: inputPhoto })
    );
    const updatedPhoto = result.data.updatePhoto;
    var foundIndex = photos.findIndex((x) => x.id === updatedPhoto.id);
    var newList = photos;
    newList[foundIndex] = updatedPhoto;
    setPhotos([]);
    setPhotos(newList);
    toast.success("保存しました");
    onClose();
  }

  /**
   * onClose
   */
  function onClose() {
    updateSlide(!isSlideOpen);
    setSelectedPhoto(null);
    setSelectedFiles(null);
    setCaption("");
  }

  return (
    <Transition.Root show={isSlideOpen} as={Fragment}>
      <Dialog
        as="div"
        static
        className="z-50 fixed inset-0 overflow-hidden"
        open={isSlideOpen}
        onClose={() => onClose()}
      >
        <div className="absolute inset-0 overflow-hidden">
          <Dialog.Overlay className="absolute inset-0" />

          <div className="fixed inset-y-0 pl-16 max-w-full right-0 flex">
            <Transition.Child
              as={Fragment}
              enter="transform transition ease-in-out duration-500 sm:duration-700"
              enterFrom="translate-x-full"
              enterTo="translate-x-0"
              leave="transform transition ease-in-out duration-500 sm:duration-700"
              leaveFrom="translate-x-0"
              leaveTo="translate-x-full"
            >
              <div className="w-screen max-w-md">
                <form className="h-full divide-y divide-gray-200 flex flex-col bg-white shadow-xl">
                  <div className="flex-1 h-0 overflow-y-auto">
                    <div className="py-6 px-4 bg-gray-300 sm:px-6">
                      <div className="flex items-center justify-between">
                        <Dialog.Title className="text-lg font-medium text-gray-900">
                          {!selectedPhoto ? "写真アップロード" : "写真修正"}
                        </Dialog.Title>
                        <div className="ml-3 h-7 flex items-center">
                          <button
                            type="button"
                            className="bg-gray-300 rounded-md text-gray-900 hover:text-gray-500 focus:outline-none"
                            onClick={() => onClose()}
                          >
                            <span className="sr-only">Close panel</span>
                            <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className="flex-1 flex flex-col justify-between">
                      <div className="px-4 divide-y divide-gray-200 sm:px-6">
                        <div className="space-y-6 pt-6 pb-5">
                          <div>
                            <h3 className="text-lg leading-6 font-medium text-gray-900">
                              {selectedPart.name +
                                " - " +
                                selectedPart.description}
                            </h3>
                          </div>
                          <div>
                            <label
                              htmlFor="photo"
                              className="block text-sm font-medium text-gray-700"
                            >
                              写真選択
                            </label>
                            <div className="mt-2 border-2 border-gray-300 border-dashed rounded-md px-6 pt-5 pb-6 justify-center">
                              <div className="space-y-1 text-center">
                                <svg
                                  className="mx-auto h-12 w-12 text-gray-400"
                                  stroke="currentColor"
                                  fill="none"
                                  viewBox="0 0 48 48"
                                  aria-hidden="true"
                                >
                                  <path
                                    d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                                <div className="text-center text-sm text-gray-600">
                                  <label
                                    htmlFor="file-upload"
                                    className="relative cursor-pointer bg-white rounded-md font-medium text-teal-600 hover:text-teal-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-teal-500"
                                  >
                                    <span>ファイルをアップロードする</span>
                                    <input
                                      id="file-upload"
                                      name="file-upload"
                                      type="file"
                                      accept="image/*"
                                      className="sr-only"
                                      onChange={(e) =>
                                        setSelectedFiles(e.target.files[0])
                                      }
                                    />
                                  </label>
                                  {/* <p className="pl-1">または、ドラッグアンドドロップします。</p> */}
                                </div>
                                <p className="text-xs text-gray-500">
                                  PNG、JPG、GIF最大10MB
                                </p>
                                {hasErrors && (
                                  <p
                                    className="mt-2 text-sm text-red-600"
                                    id="photo-error"
                                  >
                                    写真を選択してください
                                  </p>
                                )}
                                {selectedFiles && (
                                  <dd className="mt-1 text-sm text-gray-900">
                                    <ul className="border border-gray-200 rounded-md divide-y divide-gray-200">
                                      <li className="pl-3 pr-4 py-3 flex items-center justify-between text-sm">
                                        <div className="w-0 flex-1 flex items-center">
                                          {/* <!-- Heroicon name: paper-clip --> */}
                                          <PaperClipIcon className="flex-shrink-0 h-5 w-5 text-gray-400" />
                                          <span className="ml-2 flex-1 w-0 ">
                                            {selectedFiles.name}
                                          </span>
                                        </div>
                                      </li>
                                    </ul>
                                  </dd>
                                )}
                              </div>
                            </div>
                          </div>
                          <div>
                            <label
                              htmlFor="caption"
                              className="block text-sm font-medium text-gray-900"
                            >
                              キャプション
                            </label>
                            <div className="mt-1">
                              <textarea
                                id="caption"
                                name="caption"
                                rows="4"
                                className={`${
                                  hasErrors
                                    ? "pr-10 border-red-300 text-red-900 placeholder-red-300 focus:outline-none focus:ring-red-500 focus:border-red-50"
                                    : "focus:ring-teal-500 focus:border-teal-500 border-gray-300"
                                } block w-full shadow-sm sm:text-sm rounded-md`}
                                value={caption}
                                onChange={(e) => setCaption(e.target.value)}
                              ></textarea>
                            </div>
                            {hasErrors && (
                              <p
                                className="mt-2 text-sm text-red-600"
                                id="caption-error"
                              >
                                「キャプション」を入力してください
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex-shrink-0 px-4 py-4 flex justify-end">
                    <button
                      type="button"
                      className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500"
                      onClick={() => onClose()}
                    >
                      キャンセル
                    </button>
                    <button
                      type="button"
                      className="ml-4 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-teal-600 hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500"
                      onClick={() => submit()}
                    >
                      保存する
                    </button>
                  </div>
                </form>
              </div>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default PhotoUpload;
